import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEdit, faTrash, faXmark, faSearch, faFilter, faGripLines, faPlus, faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { getApiEndpoint } from '../../../../config/api';
import './Staticlines.css';
import { toast } from 'react-toastify';
import AssetDetailModal from '../shared/AssetDetailModal';
import { openLatestTestReport } from '../shared/openLatestTestReport';
import { isSuperAdmin } from '../../../../utils/roleUtils';

const STATIC_LINE_TYPES = ['Horizontal', 'Vertical', 'Diagonal', 'Custom'];
const STATIC_LINE_MATERIALS = ['Stainless Steel', 'Galvanized Steel', 'Aluminum', 'Other'];

const getBadgeClass = (status) => {
  const key = (status || 'unknown').toLowerCase();
  const map = { pass: 'rs-badge-pass', passed: 'rs-badge-passed', fail: 'rs-badge-fail', failed: 'rs-badge-failed', pending: 'rs-badge-pending', overdue: 'rs-badge-overdue', unknown: 'rs-badge-unknown' };
  return `rs-badge ${map[key] || 'rs-badge-unknown'}`;
};

const formatDate = (d) => {
  if (!d) return 'N/A';
  try { const dt = new Date(d); return isNaN(dt.getTime()) ? 'N/A' : dt.toLocaleDateString('en-GB'); }
  catch { return 'N/A'; }
};

const INITIAL_LINE = {
  systemName: '',
  location: '',
  type: STATIC_LINE_TYPES[0],
  length: '',
  material: STATIC_LINE_MATERIALS[0],
  installedBy: '',
  dateInstalled: new Date().toISOString().split('T')[0],
  lastTestDate: new Date().toISOString().split('T')[0],
  status: 'Unknown',
  certificateUrl: '',
  notes: ''
};

export default function StaticLines({ propertyId }) {
  const [staticLines, setStaticLines] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newLine, setNewLine] = useState({ ...INITIAL_LINE });
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [typeFilter, setTypeFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [detailAsset, setDetailAsset] = useState(null);

  const calculateNextTestDate = (lastTestDate) => {
    const date = new Date(lastTestDate);
    date.setMonth(date.getMonth() + 12);
    return date.toISOString().split('T')[0];
  };

  const fetchStaticLinesData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/static-lines`), {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error(`Failed to fetch static lines: ${response.status}`);

      const data = await response.json();
      const linesArray = Array.isArray(data) ? data : [data];

      setStaticLines(linesArray.map(line => ({
        id: line.id,
        propertyId: line.property_id,
        systemName: line.system_name || `Static Line ${line.id}`,
        location: line.location || line.location_description,
        type: line.type || 'Horizontal',
        length: line.length ? `${line.length}m` : 'N/A',
        material: line.material || 'N/A',
        installedBy: line.installed_by || 'N/A',
        dateInstalled: formatDate(line.date_installed),
        lastTestDate: formatDate(line.last_inspection_date),
        nextTestDate: line.next_inspection_date ? formatDate(line.next_inspection_date) : (line.last_inspection_date ? calculateNextTestDate(line.last_inspection_date) : 'N/A'),
        status: line.status || 'Unknown',
        certificateUrl: line.certificate_url,
        testReportUrl: line.test_report_url,
        notes: line.notes || ''
      })));
    } catch (err) {
      console.error('Error in fetchStaticLinesData:', err);
      setError(err.message);
      toast.error(err.message || 'Failed to fetch static lines');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLine = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/static-lines`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          system_name: newLine.systemName,
          location: newLine.location,
          type: newLine.type,
          length: newLine.length,
          material: newLine.material,
          installed_by: newLine.installedBy,
          date_installed: newLine.dateInstalled,
          last_inspection_date: newLine.lastTestDate,
          status: 'Unknown',
          certificate_url: newLine.certificateUrl,
          notes: newLine.notes
        })
      });

      if (!response.ok) throw new Error('Failed to create static line');

      setShowCreateModal(false);
      setNewLine({ ...INITIAL_LINE });
      toast.success('Static line created successfully');
      fetchStaticLinesData();
    } catch (err) {
      console.error('Error creating static line:', err);
      toast.error(err.message || 'Failed to create static line');
    }
  };

  const handleEditLine = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/static-lines/${editingLine.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          system_name: editingLine.systemName,
          location_description: editingLine.location,
          type: editingLine.type,
          length: editingLine.length,
          material: editingLine.material,
          installed_by: editingLine.installedBy,
          date_installed: editingLine.dateInstalled,
          last_inspection_date: editingLine.lastTestDate,
          status: editingLine.status,
          certificate_url: editingLine.certificateUrl,
          notes: editingLine.notes
        })
      });

      if (!response.ok) throw new Error('Failed to update static line');

      setShowEditModal(false);
      setEditingLine(null);
      toast.success('Static line updated successfully');
      fetchStaticLinesData();
    } catch (err) {
      console.error('Error updating static line:', err);
      toast.error(err.message || 'Failed to update static line');
    }
  };

  const handleDeleteLine = async (lineId) => {
    if (!window.confirm('Are you sure you want to delete this static line?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/static-lines/${lineId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete static line');

      setStaticLines(prev => prev.filter(line => line.id !== lineId));
      setShowEditModal(false);
      setEditingLine(null);
      toast.success('Static line deleted successfully');
    } catch (err) {
      console.error('Error deleting static line:', err);
      toast.error(err.message || 'Failed to delete static line');
    }
  };

  const handleViewTestData = (line) => {
    openLatestTestReport({
      propertyId,
      assetType: 'static_line',
      assetId: line.id,
      fallbackAsset: line,
      emptyMessage: 'No test data available for this static line',
      onEmpty: (msg) => toast.info(msg),
    });
  };

  useEffect(() => {
    if (propertyId) fetchStaticLinesData();
  }, [propertyId]);

  // Auto-open asset from drawing hotspot navigation
  useEffect(() => {
    const handler = (e) => {
      const { assetId, assetTable } = e.detail || {};
      if (assetTable === 'static_lines' && assetId && staticLines.length) {
        const asset = staticLines.find(a => a.id === parseInt(assetId));
        if (asset) setDetailAsset(asset);
      }
    };
    window.addEventListener('openAssetFromHotspot', handler);
    return () => window.removeEventListener('openAssetFromHotspot', handler);
  }, [staticLines]);

  // Close filter dropdown on outside click
  useEffect(() => {
    if (!showFilterDropdown) return;
    const handler = (e) => {
      if (!e.target.closest('.rs-filter-dropdown') && !e.target.closest('.filter-toggle-btn')) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFilterDropdown]);

  // Filtered data
  const filtered = staticLines
    .filter(line => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return [line.systemName, line.location, line.type, line.material, line.status].some(v => v && v.toLowerCase().includes(q));
    })
    .filter(line => typeFilter === 'All' || line.type === typeFilter);

  // ─── Error State ───
  if (error) {
    return (
      <div className="staticlines-section">
        <h1>Static Lines</h1>
        <div className="rs-error-alert">
          <FontAwesomeIcon icon={faGripLines} />
          <span>{error}</span>
          <button onClick={fetchStaticLinesData}>Retry</button>
        </div>
      </div>
    );
  }

  // ─── Loading State ───
  if (loading) {
    return (
      <div className="staticlines-section">
        <h1>Static Lines</h1>
        <div className="rs-skeleton">
          {[...Array(6)].map((_, i) => <div key={i} className="rs-skeleton-row" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="staticlines-section">
      <h1>Static Lines</h1>

      {/* Toolbar */}
      <div className="rs-toolbar">
        <div className="rs-search-bar">
          <FontAwesomeIcon icon={faSearch} className="rs-search-icon" />
          <input
            type="text"
            placeholder="Search static lines..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Table / Cards */}
      <div className="rs-table-card">
        {/* Desktop Table */}
        <div className="rs-table-scroll">
          <table className="rs-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>System Name</th>
                <th>Location</th>
                <th>Type</th>
                <th>Length</th>
                <th>Material</th>
                <th>Installed By</th>
                <th>Date Installed</th>
                <th>Last Test</th>
                <th>Next Test</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="12">
                    <div className="rs-empty-state">
                      <div className="rs-empty-icon"><FontAwesomeIcon icon={faGripLines} /></div>
                      <h3 className="rs-empty-title">
                        {staticLines.length === 0 ? 'No static lines yet' : 'No results found'}
                      </h3>
                      <p className="rs-empty-desc">
                        {staticLines.length === 0
                          ? 'Add your first static line to start tracking safety compliance.'
                          : 'Try adjusting your search or filter criteria.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : filtered.map(line => (
                <tr key={line.id}>
                  <td title={line.id}>{line.id}</td>
                  <td title={line.systemName}>{line.systemName}</td>
                  <td title={line.location}>{line.location}</td>
                  <td>{line.type}</td>
                  <td>{line.length}</td>
                  <td>{line.material}</td>
                  <td>{line.installedBy}</td>
                  <td>{line.dateInstalled}</td>
                  <td>{line.lastTestDate}</td>
                  <td>{line.nextTestDate}</td>
                  <td>
                    <span className={getBadgeClass(line.status)}>
                      {line.status || 'Unknown'}
                    </span>
                  </td>
                  <td>
                    <div className="rs-actions">
                      <button className="rs-action-btn" onClick={() => handleViewTestData(line)} title="View Test Data" aria-label="View test data">
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button className="rs-action-btn" onClick={() => setDetailAsset(line)} title="Test History" aria-label="View test history">
                        <FontAwesomeIcon icon={faClockRotateLeft} />
                      </button>
                      {isSuperAdmin() && (<>
                      <button className="rs-action-btn" onClick={() => { setEditingLine(line); setShowEditModal(true); }} title="Edit" aria-label="Edit static line">
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button className="rs-action-btn rs-action-delete" onClick={() => handleDeleteLine(line.id)} title="Delete" aria-label="Delete static line">
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                      </>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="rs-mobile-cards">
          {filtered.length === 0 ? (
            <div className="rs-empty-state">
              <div className="rs-empty-icon"><FontAwesomeIcon icon={faGripLines} /></div>
              <h3 className="rs-empty-title">
                {staticLines.length === 0 ? 'No static lines yet' : 'No results found'}
              </h3>
              <p className="rs-empty-desc">
                {staticLines.length === 0
                  ? 'Add your first static line to start tracking safety compliance.'
                  : 'Try adjusting your search or filter criteria.'}
              </p>
            </div>
          ) : filtered.map(line => (
            <div key={line.id} className="rs-mobile-card">
              <div className="rs-mobile-card-header">
                <span className="rs-mobile-card-title">{line.systemName}</span>
                <span className={getBadgeClass(line.status)}>{line.status || 'Unknown'}</span>
              </div>
              <div className="rs-mobile-card-meta">
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Location</span>
                  <span className="rs-mobile-card-value">{line.location}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Type</span>
                  <span className="rs-mobile-card-value">{line.type}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Length</span>
                  <span className="rs-mobile-card-value">{line.length}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Material</span>
                  <span className="rs-mobile-card-value">{line.material}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Last Test</span>
                  <span className="rs-mobile-card-value">{line.lastTestDate}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Next Test</span>
                  <span className="rs-mobile-card-value">{line.nextTestDate}</span>
                </div>
              </div>
              <div className="rs-mobile-card-actions">
                <button className="rs-btn rs-btn-secondary" onClick={() => handleViewTestData(line)}>
                  <FontAwesomeIcon icon={faEye} /> View
                </button>
                <button className="rs-btn rs-btn-secondary" onClick={() => setDetailAsset(line)}>
                  <FontAwesomeIcon icon={faClockRotateLeft} /> Test History
                </button>
                {isSuperAdmin() && (<>
                <button className="rs-btn rs-btn-secondary" onClick={() => { setEditingLine(line); setShowEditModal(true); }}>
                  <FontAwesomeIcon icon={faEdit} /> Edit
                </button>
                <button className="rs-btn rs-btn-danger" onClick={() => handleDeleteLine(line.id)}>
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
                </>)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="rs-modal-overlay" role="dialog" aria-modal="true" aria-label="Add new static line">
          <div className="rs-modal">
            <div className="rs-modal-header">
              <h3>Add New Static Line</h3>
              <button className="rs-modal-close" onClick={() => setShowCreateModal(false)} aria-label="Close modal">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <form onSubmit={async (e) => { e.preventDefault(); await handleCreateLine(); }}>
              <div className="rs-modal-body">
                <div className="rs-form-grid">
                  <div className="rs-form-group">
                    <label>System Name</label>
                    <input type="text" value={newLine.systemName || ''} onChange={e => setNewLine({ ...newLine, systemName: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Location</label>
                    <input type="text" value={newLine.location || ''} onChange={e => setNewLine({ ...newLine, location: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Type</label>
                    <select value={newLine.type || ''} onChange={e => setNewLine({ ...newLine, type: e.target.value })} required>
                      {STATIC_LINE_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                    </select>
                  </div>
                  <div className="rs-form-group">
                    <label>Length</label>
                    <input type="text" value={newLine.length || ''} onChange={e => setNewLine({ ...newLine, length: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Material</label>
                    <select value={newLine.material || ''} onChange={e => setNewLine({ ...newLine, material: e.target.value })} required>
                      {STATIC_LINE_MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="rs-form-group">
                    <label>Installed By</label>
                    <input type="text" value={newLine.installedBy || ''} onChange={e => setNewLine({ ...newLine, installedBy: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Date Installed</label>
                    <input type="date" value={newLine.dateInstalled || ''} onChange={e => setNewLine({ ...newLine, dateInstalled: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Last Test Date</label>
                    <input type="date" value={newLine.lastTestDate || ''} onChange={e => setNewLine({ ...newLine, lastTestDate: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Certificate URL</label>
                    <input type="text" value={newLine.certificateUrl || ''} onChange={e => setNewLine({ ...newLine, certificateUrl: e.target.value })} />
                  </div>
                  <div className="rs-form-group rs-full-width">
                    <label>Notes</label>
                    <textarea value={newLine.notes || ''} onChange={e => setNewLine({ ...newLine, notes: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="rs-modal-footer">
                <button type="button" className="rs-btn rs-btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="rs-btn rs-btn-primary">Create Static Line</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingLine && (
        <div className="rs-modal-overlay" role="dialog" aria-modal="true" aria-label="Edit static line">
          <div className="rs-modal">
            <div className="rs-modal-header">
              <h3>Edit Static Line</h3>
              <button className="rs-modal-close" onClick={() => { setShowEditModal(false); setEditingLine(null); }} aria-label="Close modal">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <form onSubmit={async (e) => { e.preventDefault(); await handleEditLine(); }}>
              <div className="rs-modal-body">
                <div className="rs-form-grid">
                  <div className="rs-form-group">
                    <label>System Name</label>
                    <input type="text" value={editingLine.systemName || ''} onChange={e => setEditingLine({ ...editingLine, systemName: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Location</label>
                    <input type="text" value={editingLine.location || ''} onChange={e => setEditingLine({ ...editingLine, location: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Type</label>
                    <select value={editingLine.type || ''} onChange={e => setEditingLine({ ...editingLine, type: e.target.value })} required>
                      {STATIC_LINE_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                    </select>
                  </div>
                  <div className="rs-form-group">
                    <label>Length</label>
                    <input type="text" value={editingLine.length || ''} onChange={e => setEditingLine({ ...editingLine, length: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Material</label>
                    <select value={editingLine.material || ''} onChange={e => setEditingLine({ ...editingLine, material: e.target.value })} required>
                      {STATIC_LINE_MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="rs-form-group">
                    <label>Installed By</label>
                    <input type="text" value={editingLine.installedBy || ''} onChange={e => setEditingLine({ ...editingLine, installedBy: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Date Installed</label>
                    <input type="date" value={editingLine.dateInstalled || ''} onChange={e => setEditingLine({ ...editingLine, dateInstalled: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Last Test Date</label>
                    <input type="date" value={editingLine.lastTestDate || ''} onChange={e => setEditingLine({ ...editingLine, lastTestDate: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Certificate URL</label>
                    <input type="text" value={editingLine.certificateUrl || ''} onChange={e => setEditingLine({ ...editingLine, certificateUrl: e.target.value })} />
                  </div>
                  <div className="rs-form-group rs-full-width">
                    <label>Notes</label>
                    <textarea value={editingLine.notes || ''} onChange={e => setEditingLine({ ...editingLine, notes: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="rs-modal-footer">
                <button type="button" className="rs-btn rs-btn-secondary" onClick={() => { setShowEditModal(false); setEditingLine(null); }}>Cancel</button>
                <button type="submit" className="rs-btn rs-btn-primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <AssetDetailModal
        isOpen={!!detailAsset}
        onClose={() => setDetailAsset(null)}
        asset={detailAsset ? { ...detailAsset, name: detailAsset.systemName, location: detailAsset.location } : null}
        assetType="static_line"
        propertyId={propertyId}
        assetMeta={detailAsset ? [
          { label: 'Type', value: detailAsset.type },
          { label: 'Length', value: detailAsset.length },
          { label: 'Material', value: detailAsset.material },
          { label: 'Installed By', value: detailAsset.installedBy }
        ] : []}
      />
    </div>
  );
}
